import { $ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { readText, readBoolean } from '../lib/form.js';

/**
 * The Reputation page.
 *
 * The three waits are typed in the repository's duration grammar (`1d`, `7d`)
 * and sent as seconds: a server that has learned to type `7d` in one field
 * should not discover it means something else here.
 */
export function initReputationPage(): void {
  const form = $<HTMLFormElement>('[data-reputation-form]');
  if (!form) return;

  const guildId = form.dataset.guildId ?? '';

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/reputation?guildId=${guildId}`, {
        guildId,
        cooldownSeconds: seconds(readText(form, 'cooldown')),
        pairCooldownSeconds: seconds(readText(form, 'pairCooldown')),
        minTenureSeconds: seconds(readText(form, 'minTenure')),
        thanksDetection: readBoolean(form, 'thanksDetection'),
      });
    },
  });
}

/**
 * Reads `7d`, `2h30m`, `45s` into seconds; a bare number is minutes.
 *
 * The same grammar the moderation durations use, deliberately — and an empty
 * field means zero, which is how a server switches a lock off rather than
 * hunting for a checkbox.
 */
function seconds(input: string): number {
  const units: Record<string, number> = { s: 1, m: 60, h: 3600, d: 86_400, w: 604_800 };
  let total = 0;

  for (const match of input.toLowerCase().matchAll(/(\d+)\s*([smhdw])/g)) {
    total += Number(match[1]) * (units[match[2] ?? ''] ?? 0);
  }

  if (total === 0 && /^\d+$/.test(input)) total = Number(input) * 60;
  return total;
}
